import React, {Fragment, useCallback, useMemo, useState} from 'react';
import {useTranslation} from "react-i18next";
import {getWebPathParam} from "../../utils/converter";
import {CONTAINER, C_CONTAINER_GENERAL_INFORMATION, LOGISTICS, SHIPPING} from "../../routes/endpoints";
import {getCustomFormat} from "../../utils/timeUtils";
import moment from "moment";
import ContainerIcon from "../../resources/icons/ContainerIcon";
import CustomerCell from "../common/table/customer-cell";
import {AIR, LAND, SEA} from "../../constants/config";
import ShippingIcon from "../../resources/icons/ShippingIcon";
import TruckIcon from "../../resources/icons/TruckIcon";
import AirIcon from "../../resources/icons/AirIcon";
import { Dialog, Transition } from "@headlessui/react";
import {ClockIcon, CubeIcon, ExternalLinkIcon} from "@heroicons/react/outline";
import XIcon from "@heroicons/react/solid/XIcon";

function CalendarLoad({data, height}) {
  const { t } = useTranslation();
  const containers = useMemo(() => data?.containers ?? [], [data?.containers]);
  const hasRows = containers.length > 0;
  const numberFormatter = useMemo(() => new Intl.NumberFormat(), []);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const groupedContainers = useMemo(() => {
    if (!hasRows) {
      return [];
    }

    const weeks = containers.reduce((acc, container) => {
      const loadMoment = moment(container.load_date, "YYYY-M-D HH:mm:ss");
      const begin = loadMoment.clone().startOf("week");
      const end = loadMoment.clone().endOf("week");
      const key = begin.format("YYYY-MM-DD");

      if (!acc[key]) {
        acc[key] = {
          key,
          label: `${begin.format("D MMMM YYYY")} - ${end.format("D MMMM YYYY")}`,
          timestamp: begin.valueOf(),
          items: []
        };
      }

      acc[key].items.push(container);
      return acc;
    }, {});

    return Object.values(weeks).sort((a, b) => b.timestamp - a.timestamp);
  }, [containers, hasRows]);

  const totalLoads = containers.length;

  const handlePreviewOpen = useCallback((url) => {
    if (!url) {
      return;
    }
    setPreviewUrl(resolveFrameUrl(url));
    setIsPreviewOpen(true);
  }, []);

  const handlePreviewClose = useCallback(() => {
    setIsPreviewOpen(false);
    setPreviewUrl(null);
  }, []);

  const wrapperStyle = height ? { height: `${height}px` } : undefined;

  if (!hasRows) {
    const emptyStyle = height ? { minHeight: `${height}px` } : undefined;
    return (
      <div style={emptyStyle} className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white/60 px-6 py-10 text-center">
        <ContainerIcon
          className="h-24 w-24 text-indigo-600"
          aria-hidden="true"
        />
        <p className="mt-4 text-2xl font-extrabold text-gray-900 sm:text-2xl sm:tracking-tight lg:text-2xl">
          {t("dashboard.calendar_load_dates.not_found_title")}
        </p>
        <p className="mx-auto mt-2 max-w-xl text-md text-gray-500">
          {t("dashboard.calendar_load_dates.not_found_description")}
        </p>
      </div>
    );
  }

  return (
    <div style={wrapperStyle} className="flex h-full flex-col">
      <div className="grid  sm:grid-cols-2">
        <SummaryCard
          variant="primary"
          label={t("dashboard.calendar_load_dates.title")}
          helper={t("dashboard.calendar_load_dates.title")}
          value={numberFormatter.format(totalLoads)}
          icon={<ContainerIcon className="h-14 w-14 text-white/80" />}
        />
        <SummaryCard
          label={t("dashboard.unconfirmed_shipping.summary_loads")}
          helper={`${t("app.container.container")} / ${t("app.container.truck")} / ${t("app.container.uld")}`}
          value={numberFormatter.format(totalLoads)}
          icon={<CubeIcon className="h-10 w-10 text-indigo-600" />}
        />
      </div>

      <nav className="flex-1 overflow-y-auto" aria-label="Scheduled containers">
        {groupedContainers.map((group) => (
          <section key={group.key} className="relative bg-white/95">
            <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-2 bg-gradient-to-r from-gray-50 to-gray-100 px-5 py-3 ">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  {t("dashboard.calendar_load_dates.title")}
                </p>
                <h3 className="text-base font-semibold text-gray-900">
                  {group.label.charAt(0).toUpperCase() + group.label.slice(1)}
                </h3>
              </div>
              <p className="text-xs font-semibold text-indigo-600">
                {t("dashboard.unconfirmed_shipping.week_summary", { count: group.items.length })}
              </p>
            </div>

            <ul className="list-none space-y-3">
              {group.items.map((item) => (
                <li key={item.id}>
                  <Row item={item} onPreview={handlePreviewOpen} />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </nav>

      <FullscreenFrame isOpen={isPreviewOpen} onClose={handlePreviewClose} url={previewUrl} />
    </div>
  );
}

const Row = ({ item, onPreview = () => {} }) => {
  const { t } = useTranslation();
  const shippingTypeMeta = useMemo(() => getShippingTypeMeta(item?.shipping_type, t), [item?.shipping_type, t]);
  const shippingTypeCode = typeof item?.shipping_type === "string" ? item?.shipping_type : item?.shipping_type?.code;
  const shippingTypeLabel = shippingTypeMeta?.name ?? getShippingTypeLabel(item?.shipping_type);
  const TypeIcon = shippingTypeMeta?.icon ?? null;
  const url = getWebPathParam([LOGISTICS, SHIPPING, item.shipping.id, CONTAINER, item.id, C_CONTAINER_GENERAL_INFORMATION]);
  const extraDetails = [
    item?.shipping?.tracking ? `${t("app.shippings.tracking")}: ${item.shipping.tracking}` : null,
    (item?.container_note ?? '').length > 0 ? item.container_note : null,
  ].filter(Boolean).join("\n");

  return (
    <article className="bg-white/90 p-4 text-sm text-gray-700 border-b">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex-1">
          <a
            href={url}
            className="block"
            onClick={(event) => {
              event.preventDefault();
              onPreview(url);
            }}
          >
            <CustomerCell customer={item.customer} extra={extraDetails || false} />
          </a>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs font-semibold">
            {
              shippingTypeLabel && (
                <span className={`inline-flex items-center gap-2 rounded-lg px-3 py-1 ${getShippingBadgeClasses(shippingTypeCode)}`}>
                  {TypeIcon && <TypeIcon className="h-4 w-4" aria-hidden="true" />}
                  {shippingTypeLabel}
                </span>
              )
            }
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 text-right">
          <span className="inline-flex items-center gap-1 text-base font-semibold text-gray-900">
            <ClockIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
            {item.load_date ? getCustomFormat(item.load_date, 'DD/MM/YYYY HH:mm') : "-"}
          </span>
          <div className="text-right">
            {renderLoadIdentifiers({ shippingTypeMeta, item, t })}
          </div>
        </div>
      </div>
    </article>
  );
};

const SummaryCard = ({ label, value, helper, icon, variant = "secondary" }) => {
  const baseClasses = "flex items-center justify-between  px-5 py-4";
  const primaryClasses = "bg-gradient-to-r from-blue-1000 to-blue-1000 text-white shadow-indigo-500/30";
  const secondaryClasses = "border border-gray-100 bg-white text-gray-900";

  return (
    <div className={`${baseClasses} ${variant === "primary" ? primaryClasses : secondaryClasses}`}>
      <div>
        <p className={`text-xs font-semibold uppercase tracking-wide ${variant === "primary" ? "text-white/70" : "text-gray-500"}`}>
          {label}
        </p>
        {
          value && (
            <p className="mt-1 text-3xl font-extrabold">
              {value}
            </p>
          )
        }
        {
          helper && (
            <p className={`mt-1 text-xs ${variant === "primary" ? "text-white/70" : "text-gray-500"}`}>
              {helper}
            </p>
          )
        }
      </div>
      <div className={`${variant === "primary" ? "text-white" : "text-blue-1000"} flex-shrink-0`}>
        {icon}
      </div>
    </div>
  );
};

const getShippingTypeMeta = (shippingType, t) => {
  const code = typeof shippingType === "string" ? shippingType : shippingType?.code;

  if (!code) {
    return null;
  }

  const shippingTypes = [
    { type: LAND, name: t("app.shippings.land_title"), containerLabel: t("app.container.truck"), icon: TruckIcon },
    { type: SEA, name: t("app.shippings.sea_title"), containerLabel: t("app.container.container"), icon: ShippingIcon },
    { type: AIR, name: t("app.shippings.air_title"), containerLabel: t("app.container.uld"), icon: AirIcon },
  ];

  return shippingTypes.find((shippingType) => shippingType.type === code) ?? null;
};

const getShippingTypeLabel = (shippingType) => {
  if (!shippingType) {
    return "";
  }

  if (typeof shippingType === "string") {
    return shippingType.toUpperCase();
  }

  if (shippingType?.name) {
    return shippingType.name;
  }

  return shippingType?.code?.toUpperCase() ?? "";
};

const getShippingBadgeClasses = (code) => {
  switch (code) {
    case SEA:
      return "bg-blue-50 text-blue-700";
    case LAND:
      return "bg-amber-50 text-amber-700";
    case AIR:
      return "bg-sky-50 text-sky-700";
    default:
      return "bg-gray-50 text-gray-600";
  }
};

const renderLoadIdentifiers = ({ shippingTypeMeta, item, t }) => {
  const shippingTypeCode = typeof item?.shipping_type === "string" ? item?.shipping_type : item?.shipping_type?.code;
  const isSeaLoad = shippingTypeCode === SEA;
  const fallbackLabel = shippingTypeMeta?.containerLabel ?? t("app.container.container_not_defined");
  const loadType = isSeaLoad
    ? (item?.container_type?.name ?? fallbackLabel)
    : (item?.delivery_type?.name ?? fallbackLabel);
  const identifier = isSeaLoad
    ? (item?.container_number ?? null)
    : (item?.license_plate ?? null);

  return (
    <>
      {
        identifier && (
          <div className="text-xs text-gray-700">
            {identifier}
          </div>
        )
      }
      <div className="text-sm font-semibold text-gray-900">
        {loadType}
      </div>
    </>
  );
};

const resolveFrameUrl = (url) => {
  if (!url) {
    return null;
  }

  if (url.startsWith("http")) {
    return url;
  }

  if (typeof window !== "undefined") {
    return `${window.location.origin}${url}`;
  }

  return url;
};

const FullscreenFrame = ({ isOpen, onClose, url }) => {
  const { t } = useTranslation();
  const frameUrl = url;

  if (!frameUrl) {
    return null;
  }

  const openInNewTab = () => {
    if (typeof window !== "undefined") {
      window.open(frameUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-[600]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Dialog.Overlay className="fixed inset-0 bg-gray-900/80 backdrop-blur" />
        </Transition.Child>

        <div className="flex min-h-screen items-stretch justify-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-6"
            enterTo="opacity-100 translate-y-0"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-6"
          >
            <Dialog.Panel className="relative flex h-screen w-screen flex-col bg-white shadow-2xl">
              <div className="flex items-center justify-between bg-blue-1000 px-6 py-4 text-white shadow">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-white/70">
                    {t("dashboard.calendar_load_dates.title")}
                  </p>
                  <p className="text-sm font-semibold">
                    {t("app.shipping_tracking.view_title")}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white transition hover:bg-white/20"
                    onClick={openInNewTab}
                  >
                    <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" />
                    <span>{t("app.common.open_in_new_tab", { defaultValue: "Apri in nuova scheda" })}</span>
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20"
                    onClick={onClose}
                  >
                    <span className="sr-only">{t("app.common.close")}</span>
                    <XIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-hidden bg-gray-100">
                <iframe
                  src={frameUrl}
                  title="Scheduled container preview"
                  className="h-full w-full border-0 bg-white"
                />
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CalendarLoad;
